import { APP_BASE_HREF } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import Bowser from 'bowser';
import { MbsPopupType, MbsSize, ModalService, ModalSettings, TextBreak, Toast, ToastService } from 'mbs-ui-kit';
import { BackupAgentUpdateRequiredModalComponent } from 'mbs-ui-shared@components/computers-modal/backup-agent-update-required-modal/backup-agent-update-required-modal.component';
import { BackupUpdateRequiredModalComponent } from 'mbs-ui-shared@components/computers-modal/backup-update-required-modal/backup-update-required-modal.component';
import { GrantLicenseForComputerModalComponent } from 'mbs-ui-shared@components/computers-modal/grant-license-for-computer-modal/grant-license-for-computer-modal.component';
import { RemoteConnectionAllowModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-allow-modal/remote-connection-allow-modal.component';
import { RemoteConnectionAuthRequiredModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-auth-required-modal/remote-connection-auth-required-modal.component';
import { RemoteConnectionChooseTypeModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-choose-type-modal/remote-connection-choose-type-modal.component';
import { RemoteConnectionDesktopLicenseSelectModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-desktop-license-select-modal/remote-connection-desktop-license-select-modal.component';
import { RemoteConnectionEnableModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-enable-modal/remote-connection-enable-modal.component';
import { RemoteConnectionInstallUpdateModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-install-update-modal/remote-connection-install-update-modal.component';
import { RemoteConnectionNotAvailableModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-not-available-modal/remote-connection-not-available-modal.component';
import { RemoteConnectionPrepareModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-prepare-modal/remote-connection-prepare-modal.component';
import { RemoteConnectionRequiredPermissionsModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-required-permissions-modal/remote-connection-required-permissions-modal.component';
import { RemoteConnectionSuperAdminModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-super-admin-modal/remote-connection-super-admin-modal.component';
import { RemoteConnectionWebLicenseSelectModalComponent } from 'mbs-ui-shared@components/computers-modal/remote-connection-web-license-select-modal/remote-connection-web-license-select-modal.component';
import { ConnectionLicenseType } from 'mbs-ui-shared@components/computers-shared/enums';
import { ManageComputerModalComponent } from 'mbs-ui-shared@components/licenses/components/manage-license-computer-modal/manage-license-computer-modal.component';
import { SelectLicenseModalComponent } from 'mbs-ui-shared@components/licenses/components/select-license-modal/select-license-modal.component';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { Administrator, LicenseType, PermissionsEnum } from 'mbs-ui-shared@models';
import Computer, { AgentType, AuthStatus, OsType } from 'mbs-ui-shared@models/Computer';
import { ConnectionType, PrepareConnectionMode } from 'mbs-ui-shared@models/ConnectionType';
import { LicenseAssignToAdmin, LicenseKind } from 'mbs-ui-shared@models/License';
import { ConnectMenuOptionModel } from 'mbs-ui-shared@models/MenuOptionModel';
import { ConnectSettingsResponse, ConnectSettingsScope, IConnectSettings } from 'mbs-ui-shared@models/connect/connect-settings.models';
import { convertToClientUrl } from 'mbs-ui-shared@pipes';
import { BuildService } from 'mbs-ui-shared@services/build-service/build.service';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { WEB_CONNECT_URL } from 'mbs-ui-shared@utils/connect-url';
import { isFontExists } from 'mbs-ui-shared@utils/isFontExists';
import { ComputerAgentWrapper } from 'mbs-ui-shared@wrappers/computer.agent.wrapper';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { BehaviorSubject, EMPTY, from, fromEvent, interval, merge, noop, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  map,
  share,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap
} from 'rxjs/operators';
import { MANUAL_ERROR_HANDLED } from '../interceptors/error-handler.interceptor';
import { POST_PAYMENT_TOKEN } from '../tokens/post-payment.token';
import { ConnectSettingsService } from './connect.settings.service';

const poolingDelay = 3000;
const isLicenseRequiredCode = 403;
const isSuitableLicenseRequired = 406;
const minimumSupportedRAAgentVersion = 320;
const connectWithTokenSupportsAgentVersion = 340;
const backupWithTokenSupportsAgentVersion = 770;
const backupMacWithTokenSupportsAgentVersion = 440;
const defaultWebConnectURL = '/WebConnect';

export const REMOTE_CONNECTION_NOT_ENABLED_ERROR_TYPE = 'https://mspbackups.com/errors/mbs/4127';
export const ALLOW_REMOTE_ACCESS_ERROR_TYPE = 'https://mspbackups.com/errors/mbs/4128';
export const REMOTE_CONNECTION_NOT_ENABLED_FOR_SUB_ADMIN_ERROR_TYPE = 'https://mspbackups.com/errors/mbs/4105';

type RetryConnectionType = { computer: Computer; type?: ConnectionType; result?: boolean; needRepeat?: boolean };

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ConnectService {
  private licensesFacade = inject(LicensesFacade);
  private ability = inject(AbilityService);
  private auth = inject(AuthFacade);
  private buildService = inject(BuildService);
  private computersFacade = inject(ComputersFacade);
  private agentWrapper = inject(ComputerAgentWrapper);
  private deviceDetectorService = inject(DeviceDetectorService);
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);
  i18nPipe = inject(I18NextPipe);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private connectSettings = inject(ConnectSettingsService);
  private apiClient = inject(HttpClient);
  private environment = inject(EnvironmentService);
  private config = inject<ConfigurationService>(ConfigurationService);
  isPostPayment = inject<Observable<boolean>>(POST_PAYMENT_TOKEN);
  webConnectPredefinedUrl = inject(WEB_CONNECT_URL);
  private appBaseHref = inject(APP_BASE_HREF);

  windowFocused: boolean;
  public currentUser: Administrator;
  public gotError$ = new Subject<boolean>();
  public connectionPrepareMode$: BehaviorSubject<PrepareConnectionMode> = new BehaviorSubject<PrepareConnectionMode>(
    PrepareConnectionMode.Installation
  );

  private deviceInfo: DeviceInfo;

  private connectRetries = 4; // retries count before showing error alert in modal
  private stopComputerPoll$ = new Subject<boolean>();
  private isProvider = false;
  private readonly settingsAPIUrl: string;
  private readonly settingsAPIParams: HttpParams;

  private menuOptionsWindows: ConnectMenuOptionModel[];
  private menuOptionsLinux: ConnectMenuOptionModel[];
  private menuOptionsMac: ConnectMenuOptionModel[];
  private connectClientUrl: string = this.environment.raDownloadURL.windows;

  constructor() {
    this.settingsAPIUrl = `${this.config.get('rmmCustomSettings')}/api/custom/provider`;
    this.settingsAPIParams = new HttpParams({
      fromObject: {
        mbsStageKey: this.config.get('mbsStageKey')
      }
    });

    this.deviceInfo = this.deviceDetectorService.getDeviceInfo();
    merge(fromEvent(window, 'focus'), fromEvent(window, 'blur'))
      .pipe(
        startWith(null),
        map(() => window.document.hasFocus()),
        distinctUntilChanged(),
        share(),
        untilDestroyed(this)
      )
      .subscribe((windowFocus) => {
        this.windowFocused = windowFocus;
      });
    this.auth.currentUser$
      .pipe(
        filter((c) => !!c),
        untilDestroyed(this)
      )
      .subscribe((user) => {
        this.currentUser = user;
        this.isProvider = user.IsProvider;
      });

    this.auth.isMBSMode
      ? this.connectSettings.getConnectClientBuild().subscribe((connectClientUrl) => {
          this.connectClientUrl = connectClientUrl;
        })
      : this.buildService
          .getAll()
          .pipe(first((data) => !!data))
          .subscribe((data) => {
            if (data?.builds?.length)
              this.connectClientUrl = data.builds.find((build) => build.buildType === 'CBLRA')?.public?.downloadLink ?? '';
          });
  }

  public getSettings(scope: ConnectSettingsScope, entityId?: string): Observable<IConnectSettings | null> {
    return this.apiClient.get(this.getSettingsAPIUrl(scope, entityId), { params: this.settingsAPIParams }).pipe(
      map((response: ConnectSettingsResponse) => {
        if (response && response.body) {
          try {
            return JSON.parse(response.body) as IConnectSettings;
          } catch (e) {
            noop;
          }
        }
        return null;
      })
    );
  }

  public getExternalDisplayUrl(computer: Computer, externalDisplayId: string): string {
    return this.router.serializeUrl(
      this.router.createUrlTree(['WebConnect/'], {
        relativeTo: this.route,
        queryParams: { hid: computer.hid, edid: externalDisplayId }
      })
    );
  }

  public deleteSettings(scope: ConnectSettingsScope, entityId: string) {
    return this.apiClient.delete(this.getSettingsAPIUrl(scope, entityId), {
      params: this.settingsAPIParams,
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  public updateSettings(settings: IConnectSettings, scope: ConnectSettingsScope, entityId?: string) {
    return this.apiClient.post(this.getSettingsAPIUrl(scope, entityId), JSON.stringify(settings), { params: this.settingsAPIParams });
  }

  private getSettingsAPIUrl(scope: ConnectSettingsScope, entityId?: string) {
    if (scope === ConnectSettingsScope.Global) {
      return `${this.settingsAPIUrl}/global/connect/host`;
    }

    return `${this.settingsAPIUrl}/${scope}/connect/host/${entityId}`;
  }

  public getMenuOptions(computer: Computer): ConnectMenuOptionModel[] {
    if (!this.menuOptionsWindows) {
      this.initMenuOptions();
    }

    switch (true) {
      case Computer.isWindows(computer):
        return this.menuOptionsWindows;
      case Computer.isMac(computer):
        return this.menuOptionsMac;
      default:
        return this.menuOptionsLinux;
    }
  }

  public getFirstMenuOption(menuOptions: ConnectMenuOptionModel[] = []): ConnectMenuOptionModel {
    return menuOptions.length ? menuOptions[0] : null;
  }

  public getFirstMenuOptionByComputer(computer: Computer): ConnectMenuOptionModel {
    return this.getFirstMenuOption(this.getMenuOptions(computer));
  }

  public isConnectAvailable(computer: Computer): boolean {
    const isOnlineAccess = !this.auth.isMBSMode;
    const isAllowedOnlineAccess =
      this.currentUser?.EnableOnlineAccessConnect && (this.ability.can('read', PermissionsEnum.ShowRemoteConnection) || this.isProvider);
    const isAllowedMBS = this.ability.can('read', PermissionsEnum.ShowRemoteConnection) || this.auth.isInternalAdmin;
    const isWindows = Computer.isWindows(computer);
    const isMac = Computer.isMac(computer);
    const isAllowedForLinux = Computer.isLinux(computer) && Computer.hasAgent(computer, AgentType.Backup);

    return (isOnlineAccess ? isAllowedOnlineAccess : isAllowedMBS) && (isWindows || isMac || isAllowedForLinux);
  }

  public isConnectDisabled(computer: Computer): boolean {
    return (
      !(
        ((Computer.isWindows(computer) || Computer.isMac(computer)) && computer?.online) ||
        Computer.isAgentOnline(computer, AgentType.Backup)
      ) || !!computer?.isBootableUsb
    );
  }

  public isConnectSettingsAvailable(computer: Computer): boolean {
    return (
      this.auth.isMBSMode &&
      !computer?.isBootableUsb &&
      ((this.ability.can('read', PermissionsEnum.ManageCompanies) && !!computer?.company) ||
        (this.ability.can('read', PermissionsEnum.ManageCompanies) && this.ability.can('read', PermissionsEnum.AccessToAllCompanies)) ||
        this.currentUser.IsProvider)
    );
  }

  public isConnectUseOldSettings(): boolean {
    return this.ability.can('read', PermissionsEnum.UseOldConnectSettings);
  }

  public isConnectGlobalSettingsAvailable(): boolean {
    return (
      this.currentUser.IsProvider ||
      (this.ability.can('read', PermissionsEnum.ManageCompanies) && this.ability.can('read', PermissionsEnum.AccessToAllCompanies))
    );
  }

  public connectAction(computerParam: Computer, connectionTypeParam?: ConnectionType) {
    if (this.currentUser.IsSuperAdmin) {
      return this.showConnectNotAvailableForSuperAdminModal();
    }

    // check os and connectionType
    return this.prepareDataForConnectAction(computerParam, connectionTypeParam).pipe(
      take(1),
      map((result) => result as RetryConnectionType),
      // refresh Computer data before connection
      switchMap((params) => this.computersFacade.getByHid$(computerParam.hid, true).pipe(map((computer) => ({ ...params, computer })))),
      // 1st check
      switchMap((params) => this.checkConnectionAllowed(params)),
      takeWhile((param) => !!param.result),
      // 2nd check (because we can't receive all error codes in 1st check)
      switchMap((params) => (params.needRepeat ? this.checkConnectionAllowed(params) : of(params))),
      takeWhile((param) => !!param.result),
      // check installed Agent
      switchMap((param) => this.checkInstalledAgent(param.computer, param.type).pipe(map((readyState) => ({ ...readyState, ...param })))),
      tap(() => this.modalService.dismissAll()),
      switchMap((param) =>
        param.type === ConnectionType.WebRTC || param.type === ConnectionType.RD
          ? this.connectByRTCorRD({
              computer: param.computer,
              connectionType: param.type,
              connectWebRtc: param.ready && !param.installation && param.type === ConnectionType.WebRTC,
              connectionPrepareMode: param.installation ? PrepareConnectionMode.Installation : PrepareConnectionMode.Preparation
            })
          : this.connectByVNCorSSH(param.computer, param.type)
      )
    );
  }

  private get isLinuxBrowser(): boolean {
    return Bowser.getParser(window.navigator.userAgent).getOSName() === 'Linux';
  }

  private initMenuOptions() {
    this.menuOptionsWindows = [
      {
        name: this.i18nPipe.transform('shared:computers.connect.connectToRCWebRTC'),
        type: ConnectionType.WebRTC
      }
    ];
    if (!this.isLinuxBrowser) {
      this.menuOptionsWindows.push({
        name: this.i18nPipe.transform('shared:computers.connect.connectToRCApp'),
        type: ConnectionType.RD
      });
    }

    this.menuOptionsLinux = [
      {
        name: this.i18nPipe.transform('shared:computers.connect.connectToRCSSH'),
        type: ConnectionType.SSH
      },
      {
        name: this.i18nPipe.transform('shared:computers.connect.connectToRCVNC'),
        type: ConnectionType.VNC
      }
    ];

    this.menuOptionsMac = [
      {
        name: this.i18nPipe.transform('shared:computers.connect.connectToRCWebRTC'),
        type: ConnectionType.WebRTC
      }
    ];
    if (!this.isLinuxBrowser) {
      this.menuOptionsMac.push({
        name: this.i18nPipe.transform('shared:computers.connect.connectToRCApp'),
        type: ConnectionType.RD
      });
    }
    this.menuOptionsMac.push({
      name: this.i18nPipe.transform('shared:computers.connect.connectToRCSSH'),
      type: ConnectionType.SSH
    });
  }

  private prepareDataForConnectAction(computer: Computer, type?: ConnectionType) {
    return of({ computer, type }).pipe(
      switchMap((params) =>
        !params.type && [OsType.apple, OsType.linux].includes(params.computer?.os)
          ? this.chooseConnectionType(params.computer).pipe(map((type) => ({ computer: params.computer, type })))
          : of(params)
      ),
      map((params) => ({
        computer: params.computer,
        type:
          ![ConnectionType.SSH, ConnectionType.VNC].includes(params.type) && [OsType.linux].includes(params.computer?.os)
            ? ConnectionType.SSH
            : ![ConnectionType.RD, ConnectionType.WebRTC].includes(params.type) && [OsType.windows].includes(params.computer?.os)
              ? ConnectionType.RD
              : params.type,
        supportedOs: [OsType.apple, OsType.linux, OsType.windows].includes(params.computer?.os),
        isWindows: params.computer?.os === OsType.windows,
        hasBackupAgent: Computer.hasAgent(params.computer, AgentType.Backup),
        isNoRequiredBackupAgentConnection: params.type == ConnectionType.WebRTC || params.type == ConnectionType.RD
      })),
      tap((params) => {
        !params.supportedOs &&
          this.toastService.error(this.i18nPipe.transform('shared:computers.connect.notSupportedOperatingSystemError'));
        params.supportedOs &&
          !params.isWindows &&
          !params.isNoRequiredBackupAgentConnection &&
          !params.hasBackupAgent &&
          this.toastService.error(this.i18nPipe.transform('shared:computers.connect.backupAgentNotInstalled'));
      }),
      filter((params) => params.supportedOs && (params.isNoRequiredBackupAgentConnection || params.hasBackupAgent))
    );
  }

  private checkConnectionAllowed(params: RetryConnectionType) {
    const defaultResult = { ...params, result: false, needRepeat: false } as RetryConnectionType;

    if (
      this.auth.isMBSMode &&
      this.ability.can('read', PermissionsEnum.RmmPerEndpoint) &&
      (params?.computer?.authStatus === AuthStatus.Pending || params?.computer?.lostAuth)
    ) {
      return this.showAuthRequiredModal(params.computer).pipe(
        tap((r) => console.log(r)),
        map(() => defaultResult),
        catchError(() => of(defaultResult))
      );
    }

    const showConnectNotAvailable = () =>
      this.showConnectNotAvailableModal(params.computer).pipe(
        catchError(() => of(false)),
        map(() => defaultResult)
      );
    const showEnableConnect = () =>
      this.showEnableConnectModal(params.computer).pipe(
        map(() => ({ result: true, needRepeat: true, ...params }) as RetryConnectionType),
        catchError(() => of(defaultResult))
      );
    const showLicenseModals = () =>
      this.selectLicense(params.computer, params.type).pipe(
        map((result) => ({ ...params, needRepeat: !!result, result: !!result }) as RetryConnectionType),
        catchError(() => of(defaultResult))
      );

    const showAllowConnect = () =>
      this.showConnectNotAllowedModal(params.computer).pipe(
        catchError(() => of(false)),
        map(() => defaultResult)
      );

    return of(true).pipe(
      switchMap((value) => (this.auth.isMBSMode ? this.computersFacade.isConnectionAllowed(params.computer.hid, params.type) : of(value))),
      map(() => ({ result: true, needRepeat: false, ...params }) as RetryConnectionType),
      catchError((error) => {
        const code = error.error.status;
        const type = error.error.type;
        const needEnable =
          type === REMOTE_CONNECTION_NOT_ENABLED_ERROR_TYPE || type === REMOTE_CONNECTION_NOT_ENABLED_FOR_SUB_ADMIN_ERROR_TYPE;
        const needAllow = type === ALLOW_REMOTE_ACCESS_ERROR_TYPE;
        const needLicense = code === isLicenseRequiredCode || code === isSuitableLicenseRequired;
        const errorTypes = [
          {
            rule: (needEnable && !this.isProvider) || (needLicense && !this.ability.can('read', PermissionsEnum.Licenses)),
            result: showConnectNotAvailable
          },
          { rule: needEnable, result: showEnableConnect },
          { rule: needLicense, result: showLicenseModals },
          { rule: needAllow, result: showAllowConnect }
        ];
        return errorTypes.find((errorType) => !!errorType.rule)?.result() ?? of(defaultResult);
      })
    );
  }

  private chooseConnectionType(computer: Computer) {
    return from(
      this.modalService.openCustom(RemoteConnectionChooseTypeModalComponent, {
        size: MbsSize.sm,
        header: this.i18nPipe.transform(`shared:computers.modals.connectTo`, { param: Computer.getComputerName(computer) }),
        data: { computer }
      })
    ).pipe(
      map((result) => result as ConnectionType),
      catchError(() => EMPTY)
    );
  }

  public getRemoteAccessNotAllowedError(computer: Computer): string {
    const company = computer.company;

    if (this.route.snapshot.queryParams.inApp === 'true') {
      return this.i18nPipe.transform('shared:computers.connect.remoteAccessNotAllowedAppVersion');
    }

    if (
      this.isProvider ||
      (this.ability.can('read', PermissionsEnum.ManageCompanies) && this.ability.can('read', PermissionsEnum.AccessToAllCompanies))
    ) {
      if (company) {
        return this.i18nPipe.transform(`shared:computers.connect.remoteAccessNotAllowedForCompanyProviderError`, {
          companyName: company.name,
          companyId: company.id
        });
      }

      return this.i18nPipe.transform(`shared:computers.connect.remoteAccessNotAllowedProviderError`);
    }

    if (company) {
      return this.i18nPipe.transform(`shared:computers.connect.remoteAccessNotAllowedForCompanyError`, { companyName: company.name });
    }

    return this.i18nPipe.transform(`shared:computers.connect.remoteAccessNotAllowedError`);
  }

  public getUseOldSettingsMenuItemInfoText() {
    if (!this.isProvider && !this.ability.can('read', PermissionsEnum.AccessToAllCompanies)) {
      return this.i18nPipe.transform(`shared:computers.connect.settingButtonMenuItemInfoForSubAdmin`);
    }

    return this.i18nPipe.transform(`shared:computers.connect.settingButtonMenuItemInfoForProvider`);
  }

  private showEnableConnectModal(computer: Computer) {
    return from(this.modalService.openCustom(RemoteConnectionEnableModalComponent, { data: { computer } }));
  }

  private showConnectNotAvailableModal(computer: Computer) {
    return from(this.modalService.openCustom(RemoteConnectionNotAvailableModalComponent, { data: { computer } }));
  }

  private showConnectNotAvailableForSuperAdminModal() {
    return from(this.modalService.openCustom(RemoteConnectionSuperAdminModalComponent));
  }

  private showConnectNotAllowedModal(computer: Computer) {
    return from(this.modalService.openCustom(RemoteConnectionAllowModalComponent, { data: { computer } }));
  }

  private showAuthRequiredModal(computer: Computer) {
    return from(this.modalService.openCustom(RemoteConnectionAuthRequiredModalComponent, { data: { computer } }));
  }

  // #region License
  private selectLicense(computer: Computer, type: ConnectionType) {
    return of({ computer, type }).pipe(
      switchMap((params) => {
        if (
          (params.type === ConnectionType.WebRTC && Computer.hasAgent(computer, AgentType.Backup)) ||
          type === ConnectionType.VNC ||
          type === ConnectionType.SSH
        ) {
          return from(
            this.modalService.openCustom(RemoteConnectionWebLicenseSelectModalComponent, {
              data: { computer, type }
            })
          ).pipe(
            switchMap((result: ConnectionLicenseType) => {
              if (result === ConnectionLicenseType.RMM && !this.ability.can('read', PermissionsEnum.RmmPerEndpoint)) {
                return this.showGrantLicenseAdminModal();
              } else if (result === ConnectionLicenseType.RMM && this.ability.can('read', PermissionsEnum.RmmPerEndpoint)) {
                return this.showSelectPELicenseModal(computer);
              } else if (result === ConnectionLicenseType.Backup) {
                return this.isPostPayment.pipe(
                  take(1),
                  switchMap((isPostPayment) => (isPostPayment ? this.showManageLicenseComputerModal(computer) : this.tryGrant(computer)))
                );
              } else {
                this.agentWrapper.handleInstallAgent(computer, AgentType.RMM);

                return of(false);
              }
            })
          );
        } else {
          return from(
            this.modalService.openCustom(RemoteConnectionDesktopLicenseSelectModalComponent, {
              data: { type }
            })
          ).pipe(
            switchMap(() =>
              this.ability.can('read', PermissionsEnum.RmmPerEndpoint)
                ? this.showSelectPELicenseModal(params.computer)
                : this.showGrantLicenseDialog(params.type)
            )
          );
        }
      })
    );
  }

  private showGrantLicenseAdminModal() {
    const licenseFilter = [LicenseType.RMM, LicenseType.RemoteDesktop];
    return this.licensesFacade
      .openAdminLicenseModal({ email: this.currentUser.Email, adminID: this.currentUser.Id, filter: licenseFilter })
      .pipe(
        take(1),
        // TODO: remove excess second fetch of assigned license below
        switchMap(() => this.licensesFacade.assignedLicenseByAdmin(this.currentUser.Id, true)),
        switchMap((license: LicenseAssignToAdmin) =>
          license?.adminId && license.license ? this.auth.fetchCurrentUser$.pipe(map(() => true)) : of(false)
        )
      );
  }

  private showSelectPELicenseModal(computer: Computer): Observable<boolean> {
    const kindForEmptyLicense = LicenseKind.Connect;
    const license = computer.connectLicense?.isExpired ? null : computer.connectLicense;
    const modal = SelectLicenseModalComponent;
    const settings = {
      data: {
        title: this.i18nPipe.transform(!license ? 'shared:licenses.management.selectLicense' : 'shared:licenses.management.changeLicense'),
        button: this.i18nPipe.transform(!license ? 'shared:buttons.assign' : 'shared:licenses.management.changeLicense'),
        computer: computer,
        licenseTypeId: license?.licenseType ?? null,
        kind: !license ? kindForEmptyLicense : license?.licenseKind,
        isAgentSupportOfflineLicenseManagement: false,
        mode: 'connect'
      }
    };

    return from(this.modalService.openCustom(modal, settings)).pipe(
      tap(() => {
        this.computersFacade.loadComputerByHid({ hid: computer?.hid, force: true });
        this.licensesFacade.invalidateLicenseTypes();
      }),
      map(() => true),
      catchError(() => of(null))
    );
  }

  private showGrantLicenseComputerModal(computer: Computer, availableLicenses = []) {
    return from(
      this.modalService.openCustom(GrantLicenseForComputerModalComponent, {
        header: 'Grant License',
        data: {
          availableLicenses,
          hid: computer.hid
        }
      })
    ).pipe(map(() => true));
  }

  private showManageLicenseComputerModal(computer: Computer) {
    return from(
      this.modalService.openCustom(ManageComputerModalComponent, {
        data: {
          title: this.i18nPipe.transform('shared:licenses.manageModal:title', {
            format: 'title'
          }),
          endpointId: computer.hid
        }
      })
    ).pipe(switchMap(() => this.auth.fetchCurrentUser$.pipe(map(() => true))));
  }

  private tryGrant(computer: Computer) {
    return this.licensesFacade.tryGrantLicense(computer.hid).pipe(
      switchMap((res) => {
        if (!res.isGranted) {
          if (res.availableLicenses.length > 0) {
            return this.showGrantLicenseComputerModal(computer, res.availableLicenses);
          }
          if (!res.availableLicenses.length && res.isAllowBuy) {
            return this.showBuyLicenseModal();
          } else {
            return of(true);
          }
        } else {
          return of(true);
        }
      })
    );
  }

  private showBuyLicenseModal() {
    const settings: ModalSettings = {
      responsive: true,
      size: MbsSize.sm,
      header: { title: this.i18nPipe.transform('shared:captions.info'), size: MbsSize.lg },
      footer: {
        okButton: {
          text: this.i18nPipe.transform('shared:licenses.licenseModal.buy'),
          type: 'success'
        },
        cancelButton: {
          text: this.i18nPipe.transform('shared:licenses.licenseModal.close')
        }
      }
    };

    return from(
      this.modalService.open(
        settings,
        `<div class="text-center">${this.i18nPipe.transform('shared:licenses.licenseModal.needLicense')}</div>`
      )
    ).pipe(
      catchError(() => of(false)),
      tap((result) => {
        if (result) {
          void this.router.navigate([RoutingPath.ApLicensesAspx], { queryParams: { buy: 'true' } });
        }
      })
    );
  }

  private showGrantLicenseDialog(connectionType: ConnectionType) {
    if (connectionType === ConnectionType.WebRTC) {
      return this.showGrantLicenseAdminModal();
    }

    const filter = connectionType === ConnectionType.RD ? [LicenseType.RMM, LicenseType.RemoteDesktop] : [LicenseType.RMM];
    return this.licensesFacade.assignLicenseDialogueModal({ email: this.currentUser.Email, adminID: this.currentUser.Id, filter }).pipe(
      switchMap((license: LicenseAssignToAdmin) => {
        return license?.adminId && license.license ? this.auth.fetchCurrentUser$.pipe(map(() => true)) : of(false);
      })
    );
  }
  // #endregion License

  private openConnectModal(computer: Computer, connectionType: ConnectionType) {
    const os = this.deviceInfo.os.toLocaleLowerCase();
    const downloadLink = this.prepareDownloadUrl(os);
    this.startPolling(computer, connectionType);
    return from(
      this.modalService
        .openCustom(RemoteConnectionPrepareModalComponent, {
          size: MbsSize.md,
          header: this.i18nPipe.transform(`shared:computers.modals.connectTo`, { param: Computer.getComputerName(computer) }),
          data: {
            downloadLink,
            computer$: this.computersFacade.getByHid$(computer.hid),
            connectionType: connectionType,
            connectionMode$: this.connectionPrepareMode$.asObservable()
          },
          footer: {
            show: false
          }
        })
        .catch(noop)
    ).pipe(
      finalize(() => {
        this.stopComputerPoll$.next(true);
        this.computersFacade.loadComputerByHid({ hid: computer.hid, force: true });
      })
    );
  }

  private prepareDownloadUrl(currentOs): string {
    const os = currentOs.toLowerCase();
    if (currentOs === 'windows') {
      return this.connectClientUrl;
    }
    if (os === 'mac') {
      return this.environment.raDownloadURL.mac;
    }
    if (os === 'ios') {
      return this.environment.raDownloadURL.ios;
    }
    if (os === 'android') {
      return this.environment.raDownloadURL.android;
    }

    return '';
  }

  private connectByVNCorSSH(computer: Computer, connectionType?: ConnectionType) {
    return of({ computer, connectionType }).pipe(
      switchMap((params) =>
        this.computersFacade.getConnectionUrl(params.computer, params.connectionType).pipe(
          map((urlData) => ({ ...params, urlData })),
          catchError((err) => {
            this.toastService.toast(
              new Toast({
                header: err.error.title,
                content: err.error.detail,
                textBreak: TextBreak.both,
                type: MbsPopupType.danger,
                showClose: true,
                delay: 0
              })
            );
            return EMPTY;
          })
        )
      ),
      tap((result) => this.openMacConnectResultModal(result.computer, result.urlData, result.connectionType))
    );
  }

  private connectByRTCorRD(params: {
    computer: Computer;
    connectionType: ConnectionType;
    connectWebRtc: boolean;
    connectionPrepareMode: PrepareConnectionMode;
  }) {
    return of(params).pipe(
      tap((param) => param.connectWebRtc && this.connectWebRTC(param.computer)),
      filter((param) => !param.connectWebRtc),
      tap((param) => this.connectionPrepareMode$.next(param.connectionPrepareMode)),
      switchMap((param) => this.openConnectModal(param.computer, param.connectionType))
    );
  }

  private openMacConnectResultModal(computer: Computer, result, connectionType: ConnectionType): void {
    let template = `<div class="row"><div class="col-3">Public IP: </div><div class="col-auto">${result.publicIp}</div></div>
                    <div class="row"><div class="col-3">Port: </div><div class="col-auto">${result.port}</div></div>
                    <div class="row"><div class="col-3">Protocol: </div><div class="col-auto">${result.protocol.toUpperCase()}</div></div>`;
    if (connectionType === ConnectionType.SSH) {
      template += `<div class="text-sm text-monospace text-break p-3 mt-2 background-secondary border">ssh -p ${result.port} ${result.publicIp}</div>`;
    } else {
      template += `<div class="text-sm text-monospace text-break p-3 mt-2 background-secondary border">vnc://${result.publicIp}:${result.port}</div>`;
    }

    this.modalService
      .open(
        {
          header: `${this.i18nPipe.transform('shared:computers.connect.connectTo')}${computer.displayName || computer.name}`,
          footer: { okButton: { show: false }, cancelButton: { text: this.i18nPipe.transform('shared:buttons.close') } }
        },
        template
      )
      .finally(noop);
  }

  private connectComputer(computer: Computer, connectionType: ConnectionType, retriesCount = 1): void {
    this.connectionPrepareMode$.next(PrepareConnectionMode.Preparation);
    this.computersFacade
      .getConnectionUrl(computer, connectionType)
      .pipe(
        takeUntil(this.stopComputerPoll$.asObservable()),
        switchMap((resp) => {
          const deepLink = typeof resp === 'string' ? resp : resp.url || resp.Url;

          return this.computersFacade.isConnectionAllowed(computer.hid, connectionType, true).pipe(
            map((result) => deepLink + (result?.result ? '&v1' : '&v0')),
            catchError(() => of(deepLink + '&v0'))
          );
        })
      )
      .subscribe({
        next: (deepLink) => {
          this.connectionPrepareMode$.next(PrepareConnectionMode.Ready);
          this.tryConnect(deepLink).catch(() => {
            const supportedOs = Object.keys(this.environment.raDownloadURL);
            const currentOs = this.deviceInfo.os.toLocaleLowerCase();
            if (!supportedOs.includes(currentOs)) {
              this.toastService.info(this.i18nPipe.transform('shared:computers.connect.osNotSupported'));
            }
          });
        },
        error: (error) => {
          if (retriesCount >= this.connectRetries) {
            error?.message && this.toastService.error(error.message);
            this.connectionPrepareMode$.next(PrepareConnectionMode.Ready);
          } else {
            this.connectComputer(computer, connectionType, ++retriesCount);
          }
        }
      });
  }

  private startPolling(computer: Computer, connectionType: ConnectionType) {
    // polling
    interval(poolingDelay)
      .pipe(
        takeUntil(this.stopComputerPoll$.asObservable()),
        tap(() => this.computersFacade.loadComputerByHid({ hid: computer.hid, quiet: true, force: true }))
      )
      .subscribe();

    this.computersFacade
      .getByHid$(computer.hid)
      .pipe(
        takeUntil(this.stopComputerPoll$.asObservable()),
        filter(
          (computer) =>
            Computer.isAgentOnline(computer, AgentType.RA) &&
            (connectionType !== ConnectionType.WebRTC || this.isAgentSupportWebRTC(computer, AgentType.RA))
        ),
        take(1)
      )
      .subscribe(() => {
        this.stopComputerPoll$.next(true);
        if (this.connectionPrepareMode$.value === PrepareConnectionMode.Installation) {
          this.connectionPrepareMode$.next(PrepareConnectionMode.Installed);
          return;
        }
        this.startConnect(computer, connectionType, true);
      });
  }

  startConnect(computer: Computer, connectionType: ConnectionType, retry = false) {
    if (connectionType === ConnectionType.WebRTC) {
      this.connectWebRTC(computer);
    } else {
      this.connectComputer(computer, connectionType, retry ? 1 : this.connectRetries);
    }
  }

  checkInstalledAgent(computer: Computer, connectionType?: ConnectionType): Observable<{ ready: boolean; installation: boolean }> {
    return of({ computer, connectionType }).pipe(
      switchMap((params) => {
        const isPreparationRequired =
          Computer.isWindows(params.computer) ||
          (Computer.isMac(params.computer) && (connectionType === ConnectionType.WebRTC || connectionType === ConnectionType.RD));

        if (isPreparationRequired) {
          const needInstallAgent = !Computer.hasAgent(params.computer, AgentType.RA);
          const needUpdateAgent =
            params.connectionType === ConnectionType.WebRTC && !this.isAgentSupportWebRTC(params.computer, AgentType.RA);

          if (needUpdateAgent && this.isShowUpdateBackupAgentInfoRequired(computer)) {
            return this.showUpdateBackupAgentRequiredModal(params.computer).pipe(
              filter(Boolean),
              map((param) => ({ ready: !!param, installation: false })),
              catchError(() => of({ ready: false, installation: false }))
            );
          }

          if (needInstallAgent && this.isShowUpdateBackupAgentBeforeUseConnectInfoRequired(computer)) {
            this.showUpdateBackupAgentBeforeUseConnectModal(computer);

            return of({ ready: false, installation: false });
          }

          if (needInstallAgent || needUpdateAgent) {
            return this.showInstallUpdateModal(params.computer, needInstallAgent).pipe(
              filter(Boolean),
              switchMap(() => (needInstallAgent ? this.installRAAgent(params.computer) : this.updateRAAgent(params.computer))),
              map((param) => ({ ready: !!param, installation: true })),
              catchError(() => of({ ready: false, installation: false }))
            );
          } else {
            if (this.isShowUpdateBackupAgentInfoRequired(computer)) {
              return this.showUpdateBackupAgentRequiredModal(params.computer).pipe(
                filter(Boolean),
                map((param) => ({ ready: !!param, installation: false })),
                catchError(() => of({ ready: false, installation: false }))
              );
            } else if (Computer.hasAgent(params.computer, AgentType.RA) && !Computer.getAgent(params.computer, AgentType.RA)?.online) {
              this.toastService.info(this.i18nPipe.transform('shared:computers.connect.connectIsOffline'));
            }
          }
        }
        return of({ ready: true, installation: false });
      }),
      filter((params) => params.ready || params.installation)
    );
  }

  private showRequiredMacPermissionModal() {
    // TODO: MBS-28497
    setTimeout(() => this.modalService.openCustom(RemoteConnectionRequiredPermissionsModalComponent));
  }

  private isSupportedAgentVersion(computer: Computer) {
    return Computer.IsSupportedAgentVersion(
      computer,
      AgentType.Backup,
      Computer.isMac(computer) ? backupMacWithTokenSupportsAgentVersion : backupWithTokenSupportsAgentVersion
    );
  }

  private isShowUpdateBackupAgentBeforeUseConnectInfoRequired(computer: Computer) {
    return (
      !Computer.hasAgent(computer, AgentType.RA) && Computer.hasAgent(computer, AgentType.Backup) && !this.isSupportedAgentVersion(computer)
    );
  }

  private isShowUpdateBackupAgentInfoRequired(computer: Computer) {
    return (
      Computer.hasAgent(computer, AgentType.RA) &&
      Computer.hasAgent(computer, AgentType.Backup) &&
      !Computer.IsSupportedAgentVersion(computer, AgentType.RA, connectWithTokenSupportsAgentVersion) &&
      !this.isSupportedAgentVersion(computer)
    );
  }

  private showUpdateBackupAgentRequiredModal(computer: Computer): Observable<boolean> {
    return from(
      this.modalService.openCustom(BackupUpdateRequiredModalComponent, {
        data: {
          computerName: Computer.getComputerName(computer)
        }
      })
    ).pipe(map((res) => !!res));
  }

  private showUpdateBackupAgentBeforeUseConnectModal(computer: Computer): void {
    this.modalService.openCustom(BackupAgentUpdateRequiredModalComponent, {
      data: {
        os: computer?.os,
        computerName: Computer.getComputerName(computer)
      }
    });
  }

  private installRAAgent(computer: Computer) {
    return this.computersFacade.installAgent(computer, AgentType.RA).pipe(
      tap((result) => {
        if (result.isErrorsInResultList) {
          const error = result.resultList.find((res) => res.result.ok === false)?.result?.message;
          if (error) {
            this.toastService.toast(
              new Toast({
                type: MbsPopupType.danger,
                content: error,
                textBreak: TextBreak.content
              })
            );
            throw new Error(error);
          }
        } else {
          Computer.isMac(computer) && this.showRequiredMacPermissionModal();
        }
      })
    );
  }

  private updateRAAgent(computer: Computer) {
    return this.computersFacade.newAgentVersionAvailable(computer.hid, AgentType.RA).pipe(
      tap(
        (buildType) => !buildType && this.toastService.error(this.i18nPipe.transform('shared:computers.connect.newVersionNotFoundError'))
      ),
      filter(Boolean),
      switchMap((buildType) => this.computersFacade.updateAgent(computer.hid, AgentType.RA, buildType))
    );
  }

  private showInstallUpdateModal(computer: Computer, isInstallMode = true): Observable<boolean> {
    return from(
      this.modalService.openCustom(RemoteConnectionInstallUpdateModalComponent, {
        data: {
          isInstallMode,
          computerName: Computer.getComputerName(computer)
        }
      })
    ).pipe(map((res) => !!res));
  }

  isAgentSupportWebRTC(computer: Computer, agentType: AgentType): boolean {
    return Computer.IsSupportedAgentVersion(computer, agentType, minimumSupportedRAAgentVersion);
  }

  get webConnectURL(): string {
    return convertToClientUrl(this.webConnectPredefinedUrl || defaultWebConnectURL, this.appBaseHref);
  }

  private connectWebRTC(computer: Computer): void {
    let url = '';
    if (location.host.includes('localhost')) {
      url = `http://localhost:3000/WebConnect?hid=${computer.hid}`;
    } else if (this.auth.isMBSMode) {
      url = this.router.serializeUrl(
        this.router.createUrlTree([this.webConnectURL], {
          relativeTo: this.route,
          queryParams: { hid: computer.hid }
        })
      );
    } else {
      url = this.router
        .serializeUrl(this.router.createUrlTree([this.webConnectURL], { relativeTo: this.route }))
        .concat(`/?hid=${computer.hid}`);
    }

    window.open(url, '_blank');
  }

  private tryConnect(url: string): Promise<void> {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    return new Promise((resolve, reject) => {
      this.windowFocused = true;
      if (this.deviceInfo.os.includes('Win') && isFontExists('CloudBerry1')) {
        // Windows
        document.location.href = url;
        resolve();
      } else if (this.deviceInfo.userAgent.includes('Macintosh') || this.deviceInfo.userAgent.includes('Firefox')) {
        // Mac and Firefox
        const f = document.createElement('iframe');
        f.style.display = 'none';
        document.body.appendChild(f);
        try {
          (f.contentWindow as Window).location.href = url;
          setTimeout(() => {
            if (this.windowFocused) {
              reject(Error());
            } else {
              resolve();
            }
          }, 2000);
        } catch (e) {
          reject(e);
        }
        setTimeout(() => {
          document.body.removeChild(f);
        }, 3000);
      } else {
        document.location.href = url;
        // eslint-disable-next-line sonarjs/no-identical-functions
        setTimeout(() => {
          if (this.windowFocused) {
            reject(Error());
          } else {
            resolve();
          }
        }, 2000);
      }
    });
  }
}
